import type { components } from "@contracts/generated/editor-service";

import {
  BIDI_CONTROL_NAMES,
  readPromptCall,
  type PromptCall,
} from "./previewPromptCall";

/**
 * Preview Insights — the Prompt Inspector's navigation (#1100).
 *
 * The approved design's §10.3 left pane: every prompt call of a run, grouped
 * by turn and agent, narrowable by turn, agent, phase and call type, and
 * paged — with no prompt body fetched for any of it. The server decides all
 * of that (`GET …/preview-runs/{ref}/prompt-calls`); this module is the
 * browser's half, and it deliberately holds the smaller half:
 *
 *  - it does NOT filter. A filter applied here would need the run, and the
 *    run is exactly what a browser must not hold (§7.4, and the Definition of
 *    Done's "no unbounded requests or DOM growth"). The filter travels as
 *    query members and the answer is already narrowed;
 *  - it does NOT sort. Grouping walks the server's list in the order it
 *    arrived and starts a new group when the coordinate changes, so a group
 *    boundary can only ever fall where the trace's own order puts one. There
 *    is no comparator here to get wrong;
 *  - it does NOT decide what is selected. The selection is an ADDRESS
 *    (`?event=`), and the detail read takes a reference rather than a
 *    position, so a poll that appends three calls, a page turn and a filter
 *    change all leave it exactly where the clinician put it.
 *
 * ## The filter is in the address, and the address names nothing private
 *
 * Four members — `turn`, `agent`, `phase`, `callType` — and they are runtime
 * coordinates: which synthetic input, which runtime agent, which conversation
 * phase, which kind of model call. None of them is a patient, candidate,
 * Liv-session or Brain-conversation identifier, and none can become one,
 * because {@link readPromptFilter} admits only a bounded, control-free token
 * and the request carries no other member (ADR 0001, ADR 0013 N6).
 *
 * A value read from the address is CALLER TEXT. It is gated for shape here —
 * bounded length, no control or bidirectional-control characters — before it
 * can reach a request, and then gated for MEANING against the counts the
 * server publishes: a value this run records no call under is reported as an
 * unsupported filter rather than rendered as an empty list, because "no such
 * agent in this run" and "no calls from that agent" are different facts.
 */

export type PreviewRunPromptCallPageResponse =
  components["schemas"]["PreviewRunPromptCallPage"];
export type PromptCallFacets =
  components["schemas"]["PreviewPromptCallFacets"];
export type PromptCallFilterSupport =
  components["schemas"]["PreviewPromptCallFilterSupport"];
export type PromptCallValueCount =
  components["schemas"]["PreviewPromptCallValueCount"];

/** The page size the panel asks for. One screenful, then a cursor. */
export const PROMPT_CALL_PAGE_LIMIT = 25;

/** The address members the filter lives in. */
export const PROMPT_FILTER_PARAMS = {
  turnNumber: "turn",
  agent: "agent",
  phase: "phase",
  callType: "callType",
} as const;

export type PromptFilterAxis = keyof typeof PROMPT_FILTER_PARAMS;

export const PROMPT_FILTER_AXES = [
  "turnNumber",
  "agent",
  "phase",
  "callType",
] as const satisfies readonly PromptFilterAxis[];

export const PROMPT_FILTER_LABELS = {
  turnNumber: "Turn",
  agent: "Agent",
  phase: "Phase",
  callType: "Call type",
} as const satisfies Record<PromptFilterAxis, string>;

export type PromptFilter = {
  turnNumber: number | null;
  agent: string | null;
  phase: string | null;
  callType: string | null;
};

/** Nothing claimed about any axis — an unfiltered read's answer, and a missing one's. */
export const NO_FILTER_SUPPORT: PromptCallFilterSupport = {
  turnNumber: null,
  agent: null,
  phase: null,
  callType: null,
};

export const NO_PROMPT_FILTER: PromptFilter = {
  turnNumber: null,
  agent: null,
  phase: null,
  callType: null,
};

/**
 * The longest filter value this browser will put on the wire, in CODE POINTS
 * — the server's own `MAX_COORDINATE_VALUE_LENGTH`, restated because it is the
 * bound an address has to be held to BEFORE a request rather than after one.
 * A longer value is dropped, not truncated: a truncated identifier is a
 * different identifier.
 */
export const MAX_FILTER_VALUE_LENGTH = 128;

/** The server's own ceiling, so an address cannot compose a 422. */
export const MAX_FILTER_TURN_NUMBER = 100_000;

/**
 * Whether a value read from an address may be sent.
 *
 * A SHAPE gate, not a grammar: this repository does not own the vocabulary
 * these values come from — they are the runtime's — so inventing a pattern
 * here would silently drop a legitimate agent the day Brain spells one
 * differently. What is refused is what no identifier ever is: empty, overlong,
 * or carrying a control or bidirectional-control character, each of which can
 * reorder or truncate the text around it wherever the value is later shown.
 *
 * COUNTED IN CODE POINTS, because that is what the bound means. The server's
 * `MAX_COORDINATE_VALUE_LENGTH` is a Pydantic/JSON-Schema length, which counts
 * code points; `String.prototype.length` counts UTF-16 code units and reports
 * 2 for every astral character. Measuring in units made this gate STRICTER
 * than the server it mirrors: an identifier of 128 emoji is one the server
 * accepts and this dropped, so the clinician's filter vanished from a valid
 * address (#1130 review, finding 4).
 *
 * The bidirectional controls are `BIDI_CONTROL_NAMES`' keys — the set the
 * exact-prompt renderer already isolates and marks — rather than a second list
 * beside it. A restatement had already drifted: it omitted U+061C, the Arabic
 * letter mark, which that renderer has always recognised.
 */
function spellable(value: string): boolean {
  const points = [...value];
  if (points.length === 0 || points.length > MAX_FILTER_VALUE_LENGTH) {
    return false;
  }
  for (const character of points) {
    if (BIDI_CONTROL_NAMES.has(character)) {
      return false;
    }
    const code = character.codePointAt(0) ?? 0;
    // C0 and C1 controls, DEL, and unpaired surrogates — the same codepoints
    // the service's shared `no_hostile_codepoints` guard refuses, spelled as
    // ranges rather than as literals so this file carries none of them.
    if (code <= 0x1f || (code >= 0x7f && code <= 0x9f)) {
      return false;
    }
    if (code >= 0xd800 && code <= 0xdfff) {
      return false;
    }
  }
  return true;
}

/** The filter an address names, with every unusable member dropped. */
export function readPromptFilter(params: URLSearchParams): PromptFilter {
  const text = (axis: PromptFilterAxis): string | null => {
    const value = params.get(PROMPT_FILTER_PARAMS[axis]);
    return value !== null && spellable(value) ? value : null;
  };
  // TURNS ARE 1-BASED, so `0` is not a turn and is dropped rather than sent —
  // the server publishes the same bound and would answer 422 for it.
  const turn = params.get(PROMPT_FILTER_PARAMS.turnNumber);
  const parsed = turn !== null && /^\d{1,6}$/.test(turn) ? Number(turn) : null;
  const turnNumber =
    parsed !== null && parsed >= 1 && parsed <= MAX_FILTER_TURN_NUMBER ? parsed : null;
  return {
    turnNumber,
    agent: text("agent"),
    phase: text("phase"),
    callType: text("callType"),
  };
}

/**
 * The address that holds this filter — built by REPLACING the four members on
 * a copy of the address the page already has, so a tab, a selected event and
 * anything a future leaf adds all survive a filter change.
 */
export function writePromptFilter(
  params: URLSearchParams,
  filter: PromptFilter,
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const axis of PROMPT_FILTER_AXES) {
    const value = filter[axis];
    if (value === null) {
      next.delete(PROMPT_FILTER_PARAMS[axis]);
    } else {
      next.set(PROMPT_FILTER_PARAMS[axis], String(value));
    }
  }
  return next;
}

/** The filter with ONE axis changed. */
export function withAxis(
  filter: PromptFilter,
  axis: PromptFilterAxis,
  value: string | null,
): PromptFilter {
  if (axis === "turnNumber") {
    return { ...filter, turnNumber: value === null ? null : Number(value) };
  }
  return { ...filter, [axis]: value };
}

/** The active value of one axis, as a string, or null. */
export function axisValue(
  filter: PromptFilter,
  axis: PromptFilterAxis,
): string | null {
  const value = filter[axis];
  return value === null ? null : String(value);
}

export function isFiltered(filter: PromptFilter): boolean {
  return PROMPT_FILTER_AXES.some((axis) => filter[axis] !== null);
}

/**
 * The query members the request carries — and, exhaustively, all of them
 * besides the page's own cursor and limit. Undefined rather than null for an
 * inactive axis, so an unnarrowed read sends no member at all.
 */
export function promptFilterQuery(filter: PromptFilter): {
  turnNumber?: number;
  agent?: string;
  phase?: string;
  callType?: string;
} {
  return {
    ...(filter.turnNumber === null ? {} : { turnNumber: filter.turnNumber }),
    ...(filter.agent === null ? {} : { agent: filter.agent }),
    ...(filter.phase === null ? {} : { phase: filter.phase }),
    ...(filter.callType === null ? {} : { callType: filter.callType }),
  };
}

// ── What one page of the navigation says ────────────────────────────────────

/**
 * A run of consecutive calls sharing a turn and an agent.
 *
 * CONSECUTIVE IN THE SERVER'S ORDER, which is the trace's order. A group is
 * therefore a stretch of the conversation, not a bucket: the same agent
 * reached again in a later turn opens a new group rather than joining an
 * earlier one, because that is what happened.
 */
export type PromptCallGroup = {
  /** Stable within one page: the coordinate plus where the group starts. */
  key: string;
  turnNumber: number | null;
  agent: string | null;
  calls: readonly PromptCall[];
};

export function groupPromptCalls(
  calls: readonly PromptCall[],
): PromptCallGroup[] {
  const groups: PromptCallGroup[] = [];
  for (const call of calls) {
    const { turnNumber, agent } = call.coordinate;
    const last = groups.at(-1);
    if (last !== undefined && last.turnNumber === turnNumber && last.agent === agent) {
      (last.calls as PromptCall[]).push(call);
      continue;
    }
    groups.push({
      key: `${turnNumber ?? "-"}/${agent ?? "-"}/${call.eventRef}`,
      turnNumber,
      agent,
      calls: [call],
    });
  }
  return groups;
}

/**
 * What a navigation read says, as a partition — the same posture
 * `locateFirstPromptCall` takes, and for the same reason: a browser must never
 * render "the run cannot be read", "the run records no prompt call" and "this
 * filter matches none" as one blank pane.
 */
export type PromptCallListing =
  | { kind: "content_unavailable" }
  | { kind: "unreadable"; at: string }
  | {
      kind: "listed";
      groups: readonly PromptCallGroup[];
      calls: readonly PromptCall[];
      /** Matching the active filter across the whole run. */
      matching: number;
      /** Held by the run, whatever the filter. */
      total: number;
      hasMore: boolean;
      nextAfterSequence: number | null;
      facets: PromptCallFacets;
      /**
       * Whether the run records anything at all under each ACTIVE filter
       * value. NOT derivable from `facets`, whose every axis is counted with
       * the other filters applied — see {@link promptFilterAxes}.
       */
      filterSupport: PromptCallFilterSupport;
    };

export function readPromptCallListing(
  response: PreviewRunPromptCallPageResponse,
): PromptCallListing {
  if (!response.run.contentAvailable) {
    return { kind: "content_unavailable" };
  }
  const page = response.page ?? null;
  if (page === null) {
    // Content promised with no page beside it: a contract disagreement this
    // build fails closed on rather than renders as a run with no calls.
    return { kind: "unreadable", at: "page" };
  }
  const calls: PromptCall[] = [];
  for (const [index, event] of page.calls.entries()) {
    const reading = readPromptCall(event);
    if (reading.kind === "unreadable") {
      // One unreadable call makes the LIST unreadable. A navigation that
      // silently dropped it would show a clinician a shorter conversation
      // than the one that happened, and the count beside it would disagree.
      return { kind: "unreadable", at: `page.calls[${index}].${reading.at}` };
    }
    calls.push(reading.call);
  }
  return {
    kind: "listed",
    groups: groupPromptCalls(calls),
    calls,
    matching: page.matching,
    total: page.total,
    hasMore: page.hasMore,
    nextAfterSequence: page.nextAfterSequence ?? null,
    facets: page.facets,
    // TOLERANT ABOUT THE MEMBER, the posture every reader on this lane takes.
    // The contract makes it required, but a response that omits it — a rolled
    // back service, a proxy that dropped it — must not take the run page down:
    // reading `support[axis]` off `undefined` threw during render, and React
    // Router replaced the whole page with its error boundary. An absent block
    // claims nothing about any axis, which is the same thing an unfiltered one
    // says, so there is no fact to lose.
    filterSupport: page.filterSupport ?? NO_FILTER_SUPPORT,
  };
}

// ── The filter controls, and what each one is allowed to claim ──────────────

export type PromptFilterOption = {
  value: string;
  label: string;
  count: number;
};

/**
 * One axis's control: what can be chosen, what IS chosen, and the two states
 * a chosen value can be in that an option list cannot express.
 */
export type PromptFilterAxisState = {
  axis: PromptFilterAxis;
  options: readonly PromptFilterOption[];
  /** The active value, or null. */
  active: string | null;
  /**
   * True when a value IS active and the run records NO call under it, whatever
   * else is filtered. The "unsupported" state this ticket owes: a pasted
   * address, a filter kept across runs, or a coordinate a later capture
   * stopped using. The remedy is to clear it — nothing will ever match.
   *
   * READ FROM THE SERVER'S `filterSupport`, never inferred from `options`. Each
   * axis's options are counted with the OTHER filters applied, so a value the
   * run records plentifully leaves the list the moment another filter excludes
   * it; inferring from that list told a clinician "this run records no call
   * with agent X" about an agent the run does record (#1130 review, finding 2).
   */
  unsupported: boolean;
  /**
   * True when a value is active, the run DOES record it, and the other active
   * filters exclude every call that carries it. A different fact with a
   * different remedy — widen the other filters — and so a different sentence.
   */
  excludedByOtherFilters: boolean;
  /**
   * How many matching calls report NO value on this axis. Never an option:
   * the server has no filter for "unreported", and offering one the request
   * cannot carry would be a control that does nothing.
   */
  unreported: number;
};

export function promptFilterAxes(
  facets: PromptCallFacets,
  support: PromptCallFilterSupport,
  filter: PromptFilter,
): PromptFilterAxisState[] {
  const options: Record<PromptFilterAxis, PromptFilterOption[]> = {
    turnNumber: facets.turns.map((turn) => ({
      value: String(turn.turnNumber),
      label: `Turn ${turn.turnNumber}`,
      count: turn.count,
    })),
    agent: facets.agents.map(asOption),
    phase: facets.phases.map(asOption),
    callType: facets.callTypes.map(asOption),
  };
  return PROMPT_FILTER_AXES.map((axis) => {
    const active = axisValue(filter, axis);
    const offered =
      active !== null && options[axis].some((option) => option.value === active);
    // `null` is "this axis is not filtered"; the server reports support only
    // for a value there is one to report for. An older service that sends no
    // member at all reads as `undefined`, and neither state is claimed as
    // "the run records nothing" — that claim needs an explicit `false`.
    // `?.` as well as the `?? NO_FILTER_SUPPORT` above: `promptFilterAxes` is
    // exported and a caller may hand it anything the contract types as this.
    const recorded = support?.[axis];
    return {
      axis,
      options: options[axis],
      active,
      unsupported: active !== null && recorded === false,
      excludedByOtherFilters: active !== null && recorded === true && !offered,
      unreported: facets.unreported[axis],
    };
  });
}

function asOption(value: PromptCallValueCount): PromptFilterOption {
  return { value: value.value, label: value.value, count: value.count };
}

/**
 * The one sentence that says how much of the run is on screen.
 *
 * DECIDED FROM WHAT IS ON SCREEN, never from `hasMore`. `hasMore` describes
 * the cursor, not the page: it is false on the LAST page of a multi-page run,
 * so reading "the whole run" off it announced forty-seven calls above a list
 * of two (#1130 review, finding 1's visible symptom). The only thing that
 * makes a page the whole run is that it holds all of it.
 *
 * ONE STRING rather than assembled nodes: a screen reader announces a live
 * region as one utterance, and a count split across elements is read as
 * disconnected fragments.
 */
export function describeListing(
  listing: Extract<PromptCallListing, { kind: "listed" }>,
  filter: PromptFilter,
): string {
  const shown = listing.calls.length;
  const calls = (n: number) => `${n} prompt ${n === 1 ? "call" : "calls"}`;
  if (!isFiltered(filter)) {
    if (shown === 0) {
      // A page past the end of the list — a cursor that outlived what it
      // pointed into. Never "the run holds nothing": it holds `total`.
      return listing.total === 0
        ? "This run holds no prompt call."
        : `Showing no prompt call on this page. The run holds ${calls(listing.total)}.`;
    }
    return shown === listing.total
      ? `Showing ${calls(listing.total)} — the whole run.`
      : `Showing ${shown} of ${calls(listing.total)} in this run.`;
  }
  if (listing.matching === 0) {
    return `No prompt call matches this filter. The run holds ${calls(listing.total)}.`;
  }
  const of = `${listing.matching} of ${calls(listing.total)}`;
  if (shown === 0) {
    return `Showing no prompt call on this page. ${of} match this filter.`;
  }
  return shown === listing.matching
    ? `Showing ${of} — every call that matches this filter.`
    : `Showing ${shown} of the ${of} that match this filter.`;
}
